/**
 * AI Context Service
 *
 * Manages secure multi-tenant AI context for COMO AI Assistant.
 * Ensures strict tenant isolation to prevent data leaks.
 *
 * CRITICAL SECURITY RULE:
 * NO AI request should EVER run without tenant_id validation.
 *
 * COMO chat: always prefers POST /api/v1/ai/chat on the Platform API (Bearer JWT).
 * Provider keys are configured from the internal app — never embedded in the customer bundle.
 */

import { customerAuthorizedFetch } from '../auth/customerSession';
import { evaluatePromptClientSide } from './aiSafetyPipeline';

export interface AIContext {
  tenant_id: string;
  project_id: string;
  monitoring_sources: MonitoringSource[];
  alert_history: AlertRecord[];
  infrastructure_metrics: InfrastructureMetrics;
}

export interface MonitoringSource {
  id: string;
  type: 'logs' | 'metrics' | 'traces' | 'uptime' | 'synthetic';
  name: string;
  status: 'healthy' | 'warning' | 'critical';
  last_update: Date;
}

export interface AlertRecord {
  id: string;
  severity: 'info' | 'warning' | 'critical';
  type: string;
  message: string;
  timestamp: Date;
  resolved: boolean;
}

export interface InfrastructureMetrics {
  cpu_usage: number;
  memory_usage: number;
  disk_usage: number;
  network_traffic: number;
  error_rate: number;
  response_time: number;
  uptime: number;
}

export interface AIRequest {
  message: string;
  context: AIContext;
  timestamp: Date;
}

export interface AIResponse {
  message: string;
  suggestions?: string[];
  actions?: AIAction[];
  severity?: 'info' | 'warning' | 'critical';
}

export interface AIAction {
  id: string;
  label: string;
  action: () => void;
}

export class AIContextService {
  /**
   * Validates that tenant context exists before AI request
   * CRITICAL: This prevents cross-tenant data leaks
   */
  static validateContext(context: AIContext): boolean {
    if (!context) {
      console.error('🚨 SECURITY: AI request blocked - no context provided');
      return false;
    }

    if (!context.tenant_id) {
      console.error('🚨 SECURITY: AI request blocked - missing tenant_id');
      return false;
    }

    if (!context.project_id) {
      console.error('🚨 SECURITY: AI request blocked - missing project_id');
      return false;
    }

    console.log('✅ SECURITY: AI context validated', {
      tenant_id: context.tenant_id,
      project_id: context.project_id
    });

    return true;
  }

  /**
   * Gets current AI context from user session
   * Extracts tenant and project from authenticated user
   */
  static getCurrentContext(): AIContext | null {
    try {
      // Get user from localStorage (coremonit_user)
      const userStr = localStorage.getItem('coremonit_user');
      if (!userStr) {
        console.warn('⚠️ No user found - cannot create AI context');
        return null;
      }

      const user = JSON.parse(userStr);
      
      // Extract tenant and project info
      // In production, this would come from user's session
      const tenant_id = user.tenant_id || user.id; // Fallback to user.id for demo
      const project_id = localStorage.getItem('current_project_id') || 'default-project';

      // Get monitoring sources (from localStorage or API)
      const monitoring_sources = this.getMonitoringSources();
      
      // Get alert history (from localStorage or API)
      const alert_history = this.getAlertHistory();
      
      // Get infrastructure metrics (from localStorage or API)
      const infrastructure_metrics = this.getInfrastructureMetrics();

      const context: AIContext = {
        tenant_id,
        project_id,
        monitoring_sources,
        alert_history,
        infrastructure_metrics
      };

      console.log('🤖 AI Context created:', {
        tenant_id: context.tenant_id,
        project_id: context.project_id,
        sources: context.monitoring_sources.length,
        alerts: context.alert_history.length
      });

      return context;
    } catch (error) {
      console.error('❌ Error creating AI context:', error);
      return null;
    }
  }

  /**
   * Gets monitoring sources for current tenant
   * In production, this would query the API with tenant_id filter
   */
  private static getMonitoringSources(): MonitoringSource[] {
    // Demo data - in production, fetch from API with tenant_id
    return [
      {
        id: 'logs-1',
        type: 'logs',
        name: 'Application Logs',
        status: 'healthy',
        last_update: new Date()
      },
      {
        id: 'metrics-1',
        type: 'metrics',
        name: 'Infrastructure Metrics',
        status: 'healthy',
        last_update: new Date()
      },
      {
        id: 'traces-1',
        type: 'traces',
        name: 'Distributed Tracing',
        status: 'healthy',
        last_update: new Date()
      }
    ];
  }

  /**
   * Gets alert history for current tenant
   * CRITICAL: Must filter by tenant_id in production
   */
  private static getAlertHistory(): AlertRecord[] {
    // Demo data - in production, fetch from API with tenant_id filter
    const alerts = localStorage.getItem('demo_alerts');
    if (alerts) {
      try {
        return JSON.parse(alerts);
      } catch (e) {
        console.warn('Failed to parse alerts from localStorage');
      }
    }

    return [];
  }

  /**
   * Gets infrastructure metrics for current tenant
   * CRITICAL: Must filter by tenant_id in production
   */
  private static getInfrastructureMetrics(): InfrastructureMetrics {
    // Demo data - in production, fetch from API with tenant_id filter
    const metrics = localStorage.getItem('demo_metrics');
    if (metrics) {
      try {
        return JSON.parse(metrics);
      } catch (e) {
        console.warn('Failed to parse metrics from localStorage');
      }
    }

    return {
      cpu_usage: 45,
      memory_usage: 62,
      disk_usage: 38,
      network_traffic: 1250,
      error_rate: 0.01,
      response_time: 120,
      uptime: 99.9
    };
  }

  /**
   * Sends AI request with context validation
   * CRITICAL: Always validates tenant context before sending
   */
  static async sendRequest(message: string): Promise<AIResponse> {
    const context = this.getCurrentContext();
    
    if (!context || !this.validateContext(context)) {
      return {
        message: '🚨 Security Error: Cannot process request without valid tenant context.',
        severity: 'critical'
      };
    }

    const pre = evaluatePromptClientSide(message);
    if (!pre.allowed) {
      return {
        message: pre.message,
        severity: 'warning',
      };
    }

    try {
      const response = await customerAuthorizedFetch('/api/v1/ai/chat', {
        method: 'POST',
        body: JSON.stringify({ prompt: message }),
      });

      if (response.ok) {
        const payload = await response.json();
        if (typeof payload?.message === 'string' && payload.message.trim()) {
          return {
            message: payload.message,
            severity: 'info',
          };
        }
      }

      if (response.status === 503) {
        let detail = 'No AI provider keys configured.';
        try {
          const err = await response.json();
          if (typeof (err as { detail?: string })?.detail === 'string') {
            detail = (err as { detail: string }).detail;
          }
        } catch {
          /* ignore */
        }
        return {
          message: `${detail} Add the COMO / AI provider key in the internal app system settings, then try again.`,
          severity: 'info',
        };
      }

      if (response.status === 502) {
        return {
          message:
            'COMO could not reach the AI provider. Please try again in a moment. Monitoring and the rest of the platform are unaffected.',
          severity: 'warning',
        };
      }

      if (response.status === 429) {
        let detail = 'Too many blocked requests. Try again in a few minutes.';
        try {
          const err = await response.json();
          if (typeof (err as { detail?: string })?.detail === 'string') {
            detail = (err as { detail: string }).detail;
          }
        } catch {
          /* ignore */
        }
        return {
          message: detail,
          severity: 'warning',
        };
      }
    } catch (error) {
      console.warn('COMO chat API request failed:', error);
    }

    const request: AIRequest = {
      message,
      context,
      timestamp: new Date()
    };

    console.log('🤖 AI Request (offline / fallback path):', {
      message: request.message,
      tenant_id: request.context.tenant_id,
      project_id: request.context.project_id
    });

    /** Client-side orchestrator uses mock LLM only; opt-in for local debugging (never for production UX). */
    const allowClientOrchestrator =
      import.meta.env.DEV === true &&
      import.meta.env.VITE_ENABLE_CLIENT_COMO_ORCHESTRATOR === 'true';

    if (allowClientOrchestrator) {
      try {
        const { ComoOrchestrator } = await import('./ComoOrchestrator');
        const orchestrator = ComoOrchestrator.getInstance();

        const user = JSON.parse(localStorage.getItem('coremonit_user') || '{}');
        const comoResponse = await orchestrator.processRequest({
          message,
          context,
          user_id: user.id || 'anonymous'
        });

        return {
          message: comoResponse.message,
          suggestions: comoResponse.suggestions,
          severity: comoResponse.severity
        };
      } catch (error) {
        console.error('❌ Error with COMO Orchestrator:', error);
      }
    }

    return this.generateMockResponse(message, context);
  }

  /**
   * Mock AI response generator
   * In production, this would be replaced with real AI API call
   */
  private static generateMockResponse(message: string, context: AIContext): AIResponse {
    const lowerMessage = message.toLowerCase();

    // Analyze infrastructure metrics
    if (lowerMessage.includes('cpu') || lowerMessage.includes('performance')) {
      const cpu = context.infrastructure_metrics.cpu_usage;
      return {
        message: `Current CPU usage is at ${cpu}%. ${cpu > 80 ? '⚠️ This is high - I recommend scaling up resources.' : '✅ Performance looks healthy!'}`,
        suggestions: cpu > 80 ? [
          'Scale up server instances',
          'Review resource-intensive processes',
          'Check for memory leaks'
        ] : ['Keep monitoring CPU trends'],
        severity: cpu > 80 ? 'warning' : 'info'
      };
    }

    if (lowerMessage.includes('memory') || lowerMessage.includes('ram')) {
      const memory = context.infrastructure_metrics.memory_usage;
      return {
        message: `Memory usage is at ${memory}%. ${memory > 80 ? '⚠️ Consider increasing memory allocation.' : '✅ Memory usage is normal.'}`,
        severity: memory > 80 ? 'warning' : 'info'
      };
    }

    if (lowerMessage.includes('alert') || lowerMessage.includes('incident')) {
      const criticalAlerts = context.alert_history.filter(a => a.severity === 'critical' && !a.resolved);
      return {
        message: criticalAlerts.length > 0 
          ? `⚠️ You have ${criticalAlerts.length} critical alert${criticalAlerts.length > 1 ? 's' : ''} that need attention.`
          : '✅ No critical alerts at the moment. All systems operational!',
        severity: criticalAlerts.length > 0 ? 'critical' : 'info'
      };
    }

    if (lowerMessage.includes('help')) {
      return {
        message: `Hi! I'm COMO, your AI infrastructure operator. I can help you with:`,
        suggestions: [
          'Analyze performance metrics',
          'Review active alerts',
          'Identify infrastructure issues',
          'Suggest optimizations',
          'Monitor system health',
          'Predict potential failures'
        ],
        severity: 'info'
      };
    }

    // Default response
    return {
      message: 'Got it. I can help with both general questions and infrastructure analysis. If you want, I can start with a quick health snapshot and current risks.',
      suggestions: [
        'Check system health',
        'Review recent alerts',
        'Analyze performance trends'
      ],
      severity: 'info'
    };
  }
}